import React, { useState, useMemo, useRef } from 'react';
import { useFormSubmit } from '../hooks/useFormSubmit';
import '../styles/template81.scss';

const DEFAULT_PRIVACY_CONTENT = `<h4>■ 개인정보처리방침</h4>
<p>관련 법령에 의거한 개인정보취급방침을 정하여 이용자 권익 보호에 최선을 다하고 있습니다.<br/>
수집한 정보는 상담 외의 용도로 활용하지 않습니다.</p>

<h4>1. 개인정보 수집 및 이용 목적</h4>
<p>
  개인정보 수집 및 이용목적: 예약 상담 및 제휴 병원 안내<br/>
  개인정보 보유 및 이용기간: 수집일로부터 5년(고객 등의 철회시 지체없이 파기)
</p>

<h4>2. 개인정보 취급 위탁</h4>
<p>
  개인정보 취급 위탁을 받는 자: 제휴 병원<br/>
  개인정보 취급 위탁 항목: 이름, 연락처, 상담 내용<br/>
  개인정보 취급 위탁 목적: 상담 및 진료 제공, 홈페이지 운영 및 관리, 이벤트 등 광고성 정보 전달
</p>

<h4>3. 자동으로 수집되는 개인정보 항목</h4>
<p>
  서비스 이용 과정에서 다음과 같은 정보가 자동으로 생성·수집될 수 있습니다.<br/>
  • 접속 로그, 쿠키, 접속 IP, 기기 정보, 브라우저 정보, 방문일시<br/>
  • 광고 식별자(Google 광고 ID, Facebook Pixel 등), 이용자 행동 데이터
</p>

<h4>4. 이용자의 권리 및 행사 방법</h4>
<p>
  이용자는 언제든지 자신의 개인정보 열람, 수정, 삭제, 처리정지를 요청할 수 있습니다.
</p>`;

/**
 * Template81 - 8단계 멀티스텝 설문형 템플릿 (498 패턴 기반)
 * 스텝 1~5: 선택 버튼 / 스텝 6: 이름 입력 / 스텝 7: 전화번호 / 스텝 8: 동의 및 제출
 */
const Template81 = ({ landingData, urlCode, urlCodeSettingId }) => {
  const { handleSubmit, isSubmitting } = useFormSubmit(
    urlCode,
    urlCodeSettingId,
    landingData?.endpoint
  );

  const [currentStep, setCurrentStep] = useState(1);
  const [collectedData, setCollectedData] = useState({});
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [nameValue, setNameValue] = useState('');
  const [phone2, setPhone2] = useState('');
  const [phone3, setPhone3] = useState('');
  const [consentPrivacy, setConsentPrivacy] = useState(false);
  const [consentMarketing, setConsentMarketing] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [marketingOpen, setMarketingOpen] = useState(false);

  const phone2Ref = useRef(null);
  const phone3Ref = useRef(null);

  const totalSteps = 8;

  // landingData에서 스텝 데이터 파싱
  const stepData = useMemo(() => {
    let surveyTopic = {};
    let surveyAnswer = {};
    try {
      surveyTopic = typeof landingData?.surveyTopic === 'string'
        ? JSON.parse(landingData.surveyTopic)
        : landingData?.surveyTopic || {};
      surveyAnswer = typeof landingData?.surveyAnswer === 'string'
        ? JSON.parse(landingData.surveyAnswer)
        : landingData?.surveyAnswer || {};
    } catch (e) {}

    const numberFormat = surveyTopic?.numberFormat || '0';

    const defaults = {
      1: { choices: [{ text: '본인', value: '본인' }, { text: '가족 혹은 지인', value: '가족 혹은 지인' }], layout: 'grid-2x1', alt: '01 누구를 위해 알아보고 계시나요?' },
      2: { choices: [{ text: '앞니', value: '앞니' }, { text: '어금니', value: '어금니' }, { text: '전체', value: '전체' }, { text: '모르겠음', value: '모르겠음' }], layout: 'grid-2x2', alt: '02 필요한 부위는 어디인가요?' },
      3: { choices: [{ text: '예', value: '예' }, { text: '아니요', value: '아니요' }], layout: 'grid-2x1', alt: '03 치과 시술을 해본 적이 있으신가요?' },
      4: { choices: [{ text: '남자', value: '남자' }, { text: '여자', value: '여자' }], layout: 'grid-2x1', alt: '04 대상자의 성별은 무엇인가요?' },
      5: { choices: [{ text: '30대', value: '30대' }, { text: '40대', value: '40대' }, { text: '50대', value: '50대' }, { text: '60대 이상', value: '60대 이상' }], layout: 'grid-2x2', alt: '05 대상자의 연령대는 무엇인가요?' },
      6: { type: 'input', placeholder: '이름', description: '60초 이내로 짧은 양식을 작성해 주시기만 하면됩니다.' },
      7: { type: 'phone', description: '60초 이내로 짧은 양식을 작성해 주시기만 하면됩니다.' },
      8: { type: 'consent' },
    };

    // surveyTopic.topic 배열 형식으로 스텝 1~5 오버라이드
    if (Array.isArray(surveyTopic?.topic)) {
      const topics = surveyTopic.topic;
      const answers = Array.isArray(surveyAnswer?.answers) ? surveyAnswer.answers : [];

      topics.forEach((t, idx) => {
        const stepNum = idx + 1;
        if (stepNum > 5) return;

        const questionText = t.text || '';
        const stepAnswers = (answers[idx] || []).filter(a => a && a.trim() !== '');

        defaults[stepNum] = { ...defaults[stepNum] };

        if (stepAnswers.length > 0) {
          defaults[stepNum].choices = stepAnswers.map(a => ({ text: a, value: a }));
          defaults[stepNum].layout = stepAnswers.length === 4 ? 'grid-2x2' : 'grid-2x1';
        }
        if (questionText) {
          const prefix = numberFormat === 'Q'
            ? `Q${stepNum}`
            : numberFormat === '0'
            ? String(stepNum).padStart(2, '0')
            : `${stepNum}`;
          defaults[stepNum].alt = `${prefix} ${questionText}`;
        }
      });
    }

    // 스텝별 이미지 매핑 (imgUrl1 → step1, ..., imgUrl8 → step8)
    for (let i = 1; i <= totalSteps; i++) {
      if (landingData?.[`imgUrl${i}`]) defaults[i].image = landingData[`imgUrl${i}`];
      if (landingData?.[`imgALT${i}`]) defaults[i].imageAlt = landingData[`imgALT${i}`];
    }

    return defaults;
  }, [landingData]);

  const currentStepData = stepData[currentStep];

  // 선택지 클릭
  const handleChoiceSelect = (stepNum, value) => {
    setSelectedChoice(value);
    setTimeout(() => {
      setCollectedData(prev => ({ ...prev, [`step${stepNum}`]: value }));
      setSelectedChoice(null);
      setCurrentStep(stepNum + 1);
    }, 500);
  };

  // 이름 다음
  const handleNameNext = () => {
    if (!nameValue.trim()) return;
    setCollectedData(prev => ({ ...prev, step6: nameValue.trim() }));
    setCurrentStep(7);
  };

  // 전화번호 다음
  const handlePhoneNext = () => {
    const fullNumber = '010' + phone2 + phone3;
    const allSame = /^(\d)\1+$/;
    if (phone2.length !== 4 || phone3.length < 4) return;
    if (allSame.test(phone2 + phone3)) {
      alert('전화번호 뒷자리에 동일한 숫자가 반복될 수 없습니다.');
      return;
    }
    setCollectedData(prev => ({ ...prev, step7: fullNumber }));
    setCurrentStep(8);
  };

  // 최종 제출
  const handleFinalSubmit = async () => {
    if (!consentPrivacy) {
      const confirmed = window.confirm(' 확인을 누르시면 개인정보처리방침에 동의하여 상담 신청이 완료됩니다.');
      if (!confirmed) return;
      setConsentPrivacy(true);
    }
    // 마케팅 정보 수신은 선택 항목 — 미동의 상태 그대로 제출한다.

    const fullPhone = collectedData.step7 || '';
    const surveyValues = [
      collectedData.step1,
      collectedData.step2,
      collectedData.step3,
      collectedData.step4,
      collectedData.step5,
    ].filter(Boolean).join(',');

    await handleSubmit({
      name: collectedData.step6 || '',
      phoneNumber: {
        first: fullPhone.slice(0, 3),
        second: fullPhone.slice(3, 7),
        third: fullPhone.slice(7),
      },
      ...(surveyValues && { selectedOption: surveyValues }),
    });
  };

  // 전체 동의 핸들러
  const handleConsentAll = (checked) => {
    setConsentPrivacy(checked);
    setConsentMarketing(checked);
  };

  // ── 스텝 렌더링 ──────────────────────────────────────────

  const renderChoiceStep = (step, stepNum) => (
    <div className={`choice-buttons ${step.layout || ''}`}>
      {(step.choices || []).map((choice, idx) => (
        <button
          key={idx}
          type="button"
          className={`choice-btn ${selectedChoice === choice.value ? 'selected' : ''}`}
          onClick={() => handleChoiceSelect(stepNum, choice.value)}
        >
          <span className="choice-text">{choice.text}</span>
        </button>
      ))}
    </div>
  );

  const renderInputStep = (step) => (
    <div className="input-form">
      <input
        type="text"
        className="form-input"
        placeholder={step.placeholder || ''}
        value={nameValue}
        onChange={e => setNameValue(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && nameValue.trim()) handleNameNext(); }}
        autoFocus
      />
      {step.description && <div className="form-description">{step.description}</div>}
      <button
        type="button"
        className="continue-btn"
        disabled={!nameValue.trim()}
        onClick={handleNameNext}
      >
        계속하기
      </button>
    </div>
  );

  const renderPhoneStep = (step) => (
    <div className="input-form">
      <div className="phone-input-wrap" style={{ display: 'flex', gap: '8px', width: '100%', maxWidth: '350px' }}>
        <input
          type="tel"
          className="form-input"
          value="010"
          readOnly
          style={{ flex: 1, textAlign: 'center', background: '#e9e9e9' }}
        />
        <input
          type="tel"
          className="form-input"
          maxLength={4}
          placeholder="0000"
          value={phone2}
          ref={phone2Ref}
          style={{ flex: 1, textAlign: 'center' }}
          onChange={e => {
            const v = e.target.value.replace(/[^0-9]/g, '');
            setPhone2(v);
            if (v.length === 4) phone3Ref.current?.focus();
          }}
          autoFocus
        />
        <input
          type="tel"
          className="form-input"
          maxLength={7}
          placeholder="0000"
          value={phone3}
          ref={phone3Ref}
          style={{ flex: 1, textAlign: 'center' }}
          onChange={e => setPhone3(e.target.value.replace(/[^0-9]/g, ''))}
          onKeyDown={e => { if (e.key === 'Backspace' && phone3.length === 0) phone2Ref.current?.focus(); }}
          onKeyPress={e => { if (e.key === 'Enter' && phone2.length === 4 && phone3.length >= 4) handlePhoneNext(); }}
        />
      </div>
      {step.description && <div className="form-description">{step.description}</div>}
      <button
        type="button"
        className="continue-btn"
        disabled={!(phone2.length === 4 && phone3.length >= 4)}
        onClick={handlePhoneNext}
      >
        계속하기
      </button>
    </div>
  );

  const calculateBenefitPeriod = () => {
    const type = landingData?.benefitDate;
    if (!type) return null;
    const today = new Date();
    let startDate, endDate;
    if (type === 'week') {
      const day = today.getDay();
      const diffToMon = day === 0 ? -6 : 1 - day;
      startDate = new Date(today);
      startDate.setDate(today.getDate() + diffToMon);
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
    } else if (type === 'month') {
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    } else {
      return null;
    }
    const fmt = (d) =>
      `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
    return `${fmt(startDate)} ~ ${fmt(endDate)}`;
  };

  const renderConsentStep = () => {
    const privacyPolicyName = landingData?.privacyPolicyName || '주식회사 퍼널커머스';
    const benefitPeriod = calculateBenefitPeriod();
    const privacyContent = landingData?.privacyPolicy
      ? landingData.privacyPolicy.replace(/\n/g, '<br/>')
      : DEFAULT_PRIVACY_CONTENT.replace(
          '<h4>■ 개인정보처리방침</h4>',
          `<h4>■ 개인정보처리방침</h4><p>개인정보 수집주체: ${privacyPolicyName}</p>`
        );

    // 전체 동의 + 마케팅 정보 수신(선택)은 전 랜딩 공통 노출 (레거시 template81Handler.js와 동일)
    const showMarketing = true;

    // 마케팅 본문은 랜스(스튜디오)에 marketingPolicy가 있으면 그 값을 우선한다
    const marketingContent = String(landingData?.marketingPolicy || '').trim()
      ? landingData.marketingPolicy.replace(/\n/g, '<br/>')
      : null;

    return (
      <div className="consent-form">

        {/* 전체 동의 */}
        {showMarketing && (
        <div className="consent-item">
          <input
            type="checkbox"
            id="consent-all"
            className="consent-checkbox"
            checked={consentPrivacy && consentMarketing}
            onChange={e => handleConsentAll(e.target.checked)}
          />
          <div className="consent-content">
            <div className="consent-title">전체 동의</div>
          </div>
        </div>
        )}

        {/* 개인정보 동의 */}
        <div className="consent-item">
          <input
            type="checkbox"
            id="consent-privacy"
            className="consent-checkbox"
            checked={consentPrivacy}
            onChange={e => {
              setConsentPrivacy(e.target.checked);
            }}
          />
          <div className="consent-content">
            <div className="consent-header">
              <div className="consent-title">상담/예약 서비스 제공 동의 (필수)</div>
              <button
                type="button"
                className="detail-toggle"
                onClick={() => { setPrivacyOpen(prev => !prev); setMarketingOpen(false); }}
              >
                {privacyOpen ? '닫기' : '자세히보기'}
              </button>
            </div>
            <div className={`consent-detail ${privacyOpen ? 'open' : ''}`}>
              <div
                className="consent-detail-content"
                dangerouslySetInnerHTML={{ __html: privacyContent }}
              />
            </div>
          </div>
        </div>

        {/* 마케팅 동의 */}
        {showMarketing && (
        <div className="consent-item">
          <input
            type="checkbox"
            id="consent-marketing"
            className="consent-checkbox"
            checked={consentMarketing}
            onChange={e => {
              setConsentMarketing(e.target.checked);
            }}
          />
          <div className="consent-content">
            <div className="consent-header">
              <div className="consent-title">마케팅 정보 수신 (선택)</div>
              <button
                type="button"
                className="detail-toggle"
                onClick={() => { setMarketingOpen(prev => !prev); setPrivacyOpen(false); }}
              >
                {marketingOpen ? '닫기' : '자세히보기'}
              </button>
            </div>
            <div className={`consent-detail ${marketingOpen ? 'open' : ''}`}>
              {marketingContent ? (
                <div
                  className="consent-detail-content"
                  dangerouslySetInnerHTML={{ __html: marketingContent }}
                />
              ) : (
              <div className="consent-detail-content">
                <h4>[마케팅 활용 및 수신 동의]</h4>
                <p>
                  *이용자는 개인정보에 대해 마케팅 활용 동의 및 수신 거부할 권리가 있으나 동의를 거부할 경우 상품 및 이벤트 정보, 이벤트 참여, 상담 등에 제약이 발생할 수 있습니다.
                </p>
              </div>
              )}
            </div>
          </div>
        </div>
        )}

        <div className="consent-note">
          ※ 상담/예약 서비스 제공 동의는 필수 항목입니다.<br />
          {showMarketing && '※ 마케팅 정보 수신은 선택 항목이며, 동의하지 않아도 신청할 수 있습니다.'}
        </div>

        <button
          type="button"
          className="submit-btn"
          disabled={isSubmitting}
          onClick={handleFinalSubmit}
        >
          {isSubmitting ? '제출중...' : '설문제출완료'}
        </button>
      </div>
    );
  };

  const renderCurrentStep = () => {
    if (!currentStepData) return null;

    if (currentStepData.type === 'consent') return renderConsentStep();
    if (currentStepData.type === 'phone') return renderPhoneStep(currentStepData);
    if (currentStepData.type === 'input') return renderInputStep(currentStepData);
    return renderChoiceStep(currentStepData, currentStep);
  };

  return (
    <section id="template81" className="template81-layout">
      {isSubmitting && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 99998,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.55)',
        }}>
          <style>{`@keyframes zentorgaSpin { to { transform: rotate(360deg) } }`}</style>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '48px', height: '48px',
              border: '4px solid rgba(255,255,255,0.3)',
              borderTopColor: '#fff',
              borderRadius: '50%',
              animation: 'zentorgaSpin 0.8s linear infinite',
            }} />
            <p style={{ color: '#fff', fontSize: '16px', fontWeight: 600, margin: 0 }}>신청 중입니다</p>
          </div>
        </div>
      )}
      <div className="multistep-container style-81">
        <div className="main-image-area">
          {currentStepData?.image ? (
            <img
              src={currentStepData.image}
              className="main-image"
              alt={currentStepData.imageAlt || ''}
            />
          ) : (
            <img className="main-image" alt="" style={{ display: 'none' }} />
          )}
          {renderCurrentStep()}
          {landingData.eventTarget && currentStepData?.type === 'consent' && (
            <div className="eventTarget">
              <p>이벤트 대상자 : <span className="event-target-text">{landingData.eventTarget}</span></p>
            </div>
          )}
          {currentStepData?.type === 'consent' && calculateBenefitPeriod() && (
            <div className="benefit-period-bar">혜택 기간: {calculateBenefitPeriod()}</div>
          )}
        </div>
        {landingData.footerClassName && landingData.footerDetail && (
          <footer className={`footerClassName ${landingData.footerClassName || ''}`}>
            {landingData.footerDetail && (
              <p className="footerDetail" style={{ whiteSpace: 'pre-line' }}>
                {landingData.footerDetail}
              </p>
            )}
          </footer>
        )}
      </div>
    </section>
  );
};

export default Template81;
